import { PortfolioCreationFlow, PortfolioFieldKey, PortfolioTypeRule } from 'pco-product-service-internal-consumer';
import { FieldRule } from '../../types/portfolios/portfolioFieldData';
import {
  PORTFOLIO_BASE_FIELDS,
  PORTFOLIO_TYPE_DEPENDENT_FIELDS,
  PortfolioBaseField,
  PortfolioTypeDependentField,
} from './portfolioTypeDependentFields';

export type ResolvedFieldRules = Record<PortfolioBaseField | PortfolioTypeDependentField, FieldRule>;

type ExistingPortfolio = { canEditDetails?: boolean };

type ResolveFieldRulesParams = {
  rules: readonly PortfolioTypeRule[];
  portfolioType: string;
  isStandaloneCreate: boolean;
  isEdit: boolean;
  existingPortfolio?: ExistingPortfolio;
};

const HIDDEN_RULE: FieldRule = { state: 'hidden' };

const apiRule = (
  rules: readonly PortfolioTypeRule[],
  portfolioType: string,
  apiField: PortfolioFieldKey,
): FieldRule => {
  const rule = rules
    .find((typeRule) => typeRule.portfolioType === portfolioType)
    ?.fields.find((fieldRule) => fieldRule.field === apiField);
  return rule?.visible
    ? { state: rule.editable ? 'editable' : 'disabled', required: rule.mandatory, value: rule.defaultValue }
    : HIDDEN_RULE;
};

const mapFields = <Field extends string, Descriptor, Value>(
  descriptors: Record<Field, Descriptor>,
  toValue: (descriptor: Descriptor, field: Field) => Value,
) =>
  Object.fromEntries(
    (Object.entries(descriptors) as [Field, Descriptor][]).map(([field, descriptor]) => [
      field,
      toValue(descriptor, field),
    ]),
  ) as Record<Field, Value>;

export const resolveFieldRules = ({
  rules,
  portfolioType,
  isStandaloneCreate,
  isEdit,
  existingPortfolio,
}: ResolveFieldRulesParams): ResolvedFieldRules => ({
  ...mapFields(PORTFOLIO_BASE_FIELDS, ({ apiField, fixedRule }) =>
    isStandaloneCreate ? apiRule(rules, portfolioType, apiField) : fixedRule(isEdit),
  ),
  ...mapFields(PORTFOLIO_TYPE_DEPENDENT_FIELDS, ({ apiField, localRulesByPortfolioType }, field): FieldRule => {
    const typeRule =
      apiField === undefined
        ? (localRulesByPortfolioType?.[portfolioType] ?? HIDDEN_RULE)
        : apiRule(rules, portfolioType, apiField);
    if (field === 'details' && existingPortfolio) {
      return { ...typeRule, state: existingPortfolio.canEditDetails === true ? 'editable' : 'disabled' };
    }
    return isStandaloneCreate ? typeRule : HIDDEN_RULE;
  }),
});

export const resolveServicingRoleRule = (rules: readonly PortfolioTypeRule[], portfolioType: string): FieldRule =>
  apiRule(rules, portfolioType, PortfolioFieldKey.ServicingRole);

type ServicingRoleVisibilityParams = {
  rules: readonly PortfolioTypeRule[];
  portfolioType: string;
  isStandaloneCreate: boolean;
  rocEnabled: boolean;
};

export const isServicingRoleVisible = ({
  rules,
  portfolioType,
  isStandaloneCreate,
  rocEnabled,
}: ServicingRoleVisibilityParams): boolean =>
  isStandaloneCreate ? resolveServicingRoleRule(rules, portfolioType).state !== 'hidden' : rocEnabled;

export const portfolioTypesForFlow = (rules: readonly PortfolioTypeRule[], creationFlow: PortfolioCreationFlow) =>
  rules.filter((rule) => rule.creationFlow === creationFlow).map((rule) => rule.portfolioType);

export const getFieldValues = (params: ResolveFieldRulesParams) => {
  const fieldRules = resolveFieldRules(params);

  const values = mapFields(PORTFOLIO_TYPE_DEPENDENT_FIELDS, (_, field) => fieldRules[field].value ?? '');

  return values;
};
